import * as React from 'react';
import { useState } from 'react';
import {
  List,
  Datagrid,
  FunctionField,
  useNotify,
  EditButton,
  Confirm,
  useRefresh,
  useListContext,
  useGetList,
} from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useLocation } from 'react-router-dom';
import { Box, ButtonBase, Chip, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../icon-button/IconButton';
import { useNavigate, useParams } from 'react-router-dom';
import { Switch } from '../switch/Switch';
import { CardActionIcons } from '../card-actions/CardActions';
import { BackButton } from '../back-button/BackButton';
import { EmptyBox } from '../empty-box/EmptyBox';
import { cardActionButtonSx } from '../../core/theme/buttonStyles';
import { CategoryAddDialog } from '../dialogs/CategoryAddDialog';
import { RestaurantItemService } from '../../services/restaurantItem/restaurantItemService';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import * as Sentry from '@sentry/react';
import { PageHeader } from '../page-header/PageHeader';
import { listSurfaceSx } from '../../core/theme/listStyles';

//TODO: move styles to a separate file if possible

const ItemImage = ({ url, size = 72 }: { url?: string | null; size?: number }) => {
  if (!url) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: 'var(--radius-md)',
          border: '1px dashed var(--color-border-strong)',
          backgroundColor: 'var(--color-bg-subtle)',
        }}
      />
    );
  }

  return (
    <Box
      component="img"
      src={url}
      alt=""
      loading="lazy"
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 'var(--radius-md)',
        objectFit: 'cover',
        border: '1px solid var(--color-secondary)',
        backgroundColor: 'var(--color-bg-subtle)',
      }}
    />
  );
};

const LocationItemsTableActions = ({ restaurantImageUrl }: { restaurantImageUrl?: string }) => {
  const location = useLocation();
  const locationName = (location.state as { locationName?: string } | null)?.locationName;
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');

  const [open, setOpen] = React.useState(false);

  return (
    <Box>
      <PageHeader
        title={t('resources.itemsManagement')}
        subtitle={locationName}
        stretchActionsOnMobile
        back={
          <>
            <BackButton />
            {!isMobile && <ItemImage url={restaurantImageUrl} />}
          </>
        }
        actions={
          <>
            <IconButton
              icon={<CategoryOutlinedIcon />}
              label={t('locationItems.manageCategories')}
              sx={isMobile ? cardActionButtonSx : undefined}
              onClick={() => {
                navigate(`/locationManagement/${id}/categories`, {
                  state: { locationName: locationName },
                });
              }}
            />
            <IconButton
              icon={<SwapVertIcon />}
              label={t('menuOrder.action')}
              sx={isMobile ? cardActionButtonSx : undefined}
              onClick={() => {
                navigate(`/locationManagement/${id}/menu-order`, {
                  state: { locationName: locationName },
                });
              }}
            />
            <IconButton
              icon={<AddIcon />}
              label={t('locationItems.addItems')}
              sx={isMobile ? cardActionButtonSx : undefined}
              onClick={() => navigate(`/locationsManagement/${id}/items/create`)}
            />
          </>
        }
      />
      <CategoryAddDialog open={open} onClose={() => setOpen(false)} />
    </Box>
  );
};

/* Przelacznik to reczne "brak na stanie" restauracji. Harmonogram dziala obok niego, wiec
   bez tego znacznika produkt wlaczony, ale poza godzinami/datami harmonogramu wygladal na
   liscie jak dostepny, choc klient go nie widzi. */
const ScheduleChip = ({ record }: { record: any }) => {
  const { t } = useTranslation();
  if (!record.hasSchedule) return null;

  const outside = record.isAvailable && !record.availableNow;
  return (
    <Tooltip
      title={t(outside ? 'locationItems.schedule.outsideHint' : 'locationItems.schedule.activeHint')}
    >
      <Chip
        size="small"
        label={t(outside ? 'locationItems.schedule.outside' : 'locationItems.schedule.active')}
        sx={
          outside
            ? { backgroundColor: 'var(--color-warning-soft)', color: 'var(--color-warning)' }
            : { backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-secondary)' }
        }
      />
    </Tooltip>
  );
};

/* Produkt z wylaczonym "Dostepny w dostawie" widac tylko w menu QR, nie w aplikacji. */
const MenuOnlyChip = ({ record }: { record: any }) => {
  const { t } = useTranslation();
  if (record.isAvailableForDelivery !== false) return null;

  return (
    <Tooltip title={t('locationItems.menuOnly.hint')}>
      <Chip
        size="small"
        label={t('locationItems.menuOnly.label')}
        sx={{ backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-secondary)' }}
      />
    </Tooltip>
  );
};

interface MobileItemsListProps {
  switchStates: { [key: number]: boolean };
  lockUnLockItem: (itemId: number, currentState: boolean) => () => void;
  id: string | undefined;
  setConfirmOpen: (open: boolean) => void;
  setToDelete: (id: number | null) => void;
}

const MobileItemsList = ({
  switchStates,
  lockUnLockItem,
  id,
  setConfirmOpen,
  setToDelete,
}: MobileItemsListProps) => {
  const { data, isLoading } = useListContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (isLoading || !data) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {data.map((record: any) => {
        const originalStatus: boolean = record.isAvailable;
        const currentStatus =
          switchStates[record.id] !== undefined ? switchStates[record.id] : originalStatus;

        return (
          <Box
            key={record.id}
            sx={{
              border: '1px solid var(--color-secondary)',
              borderRadius: 'var(--radius-xs)',
              p: 'var(--space-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              {/* Zdjecie i nazwa prowadza wprost do edycji pozycji — to najwieksze
                  cele na karcie. Wczesniej otwieraly ta sama szuflade co kebab; po
                  jej usunieciu edycja jest jedyna sensowna akcja "wejdz w rekord".
                  ButtonBase, nie Box z onClick, bo daje fokus z klawiatury i rolę
                  przycisku za darmo. */}
              {/* Bez aria-label — nazwa dostepna bierze sie z zawartosci ("Margharita
                  Inne"), co czytnik ekranu mowi sensowniej niz generyczne "Edytuj".
                  Rolę przycisku niesie samo ButtonBase. */}
              <ButtonBase
                onClick={() => navigate(`/locationsManagement/${id}/items/${record.id}`)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  textAlign: 'left',
                  borderRadius: 'var(--radius-md)',
                  '&:hover': { backgroundColor: 'var(--color-bg-subtle)' },
                }}
              >
                <ItemImage url={record.imageUrl} size={48} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>
                    {record.name ?? ''}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
                  >
                    {(record.category ?? '').trim()}
                  </Typography>
                </Box>
              </ButtonBase>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, flexShrink: 0 }}>
                {record.price}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pt: 'var(--space-1)',
                borderTop: '1px solid var(--color-hairline)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                  {t('locationItems.columns.availability')}
                </Typography>
                <Switch
                  checked={currentStatus}
                  color="primary"
                  size="small"
                  onChange={() => lockUnLockItem(record.id, currentStatus)()}
                />
                <ScheduleChip record={record} />
                <MenuOnlyChip record={record} />
              </Stack>
              <CardActionIcons
                actions={[
                  {
                    key: 'edit',
                    label: t('common.edit'),
                    icon: <EditIcon fontSize="small" />,
                    onClick: () => navigate(`/locationsManagement/${id}/items/${record.id}`),
                  },
                  {
                    key: 'delete',
                    label: t('common.delete'),
                    icon: <DeleteOutlineIcon fontSize="small" />,
                    destructive: true,
                    onClick: () => {
                      setToDelete(record.id ?? null);
                      setConfirmOpen(true);
                    },
                  },
                ]}
              />
            </Box>
          </Box>
        );
      })}

    </Box>
  );
};

export const LocationItemsTable = () => {
  const refresh = useRefresh();
  const { t } = useTranslation();
  const { id } = useParams();
  const [switchStates, setSwitchStates] = useState<{ [key: number]: boolean }>({});
  const restaurantItemService = new RestaurantItemService();
  const notify = useNotify();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDelete, setToDelete] = React.useState<number | null>(null);
  const isMobile = useMediaQuery('(max-width:600px)');

  // The items response also carries the restaurant image at the list level;
  // read it from the query meta (deduped against the <List> fetch) for the header.
  const { meta } = useGetList('itemsManagement', {
    pagination: { page: 1, perPage: 25 },
    filter: { restaurantId: id },
  });
  const restaurantImageUrl = (meta as { restaurantImageUrl?: string } | undefined)?.restaurantImageUrl;

  const lockUnLockItem = (itemId: number, currentState: boolean) => async () => {
    setSwitchStates((prev) => ({
      ...prev,
      [itemId]: !currentState,
    }));

    try {
      await restaurantItemService.lockUnlockItem(itemId);
    } catch (error: any) {
      setSwitchStates((prev) => ({
        ...prev,
        [itemId]: currentState,
      }));
      notify(error.response.data.error, { type: 'error' });
      console.error(error.response.data.error);
      Sentry.captureException(error);
    }
  };
  const resource = 'locationsManagement/' + id + '/items';
  return (
    <Box>
      <LocationItemsTableActions restaurantImageUrl={restaurantImageUrl} />
      <List
        title={t('resources.itemsManagement')}
        actions={false}
        filter={{ restaurantId: id }}
        empty={
          <EmptyBox
            title={t('locationItems.emptyBox.title')}
            description={t('locationItems.emptyBox.description')}
            resource={resource}
          />
        }
        perPage={25}
        resource="itemsManagement"
        sx={listSurfaceSx({ isMobile })}
      >
        {isMobile ? (
          <MobileItemsList
            switchStates={switchStates}
            lockUnLockItem={lockUnLockItem}
            id={id}
            setConfirmOpen={setConfirmOpen}
            setToDelete={setToDelete}
          />
        ) : (
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
            sx={{
              '& .RaDatagrid-headerCell': {
                fontWeight: 700,
                letterSpacing: 0.2,
              },
            }}
          >
            <FunctionField
              sortable={false}
              label=""
              render={(record: any) => (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <ItemImage url={record?.imageUrl} size={40} />
                </Box>
              )}
            />
            <FunctionField
              sortable={false}
              label={t('locationItems.columns.name')}
              render={(record: any) => record?.name ?? ''}
            />
            <FunctionField
              sortable={false}
              label={t('locationItems.columns.category')}
              render={(record: any) => (record?.category ?? '').trim()}
            />
            <FunctionField
              sortable={false}
              label={t('locationItems.columns.price')}
              render={(record: any) => record?.price}
            />
            <FunctionField
              sortable={false}
              label={t('locationItems.columns.availability')}
              render={(record: any) => {
                const originalStatus: boolean = record.isAvailable;
                const currentStatus =
                  switchStates[record.id] !== undefined ? switchStates[record.id] : originalStatus;

                return (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Switch
                      checked={currentStatus}
                      color="primary"
                      size="small"
                      onChange={() => lockUnLockItem(record.id, currentStatus)()}
                    />
                    <ScheduleChip record={record} />
                    <MenuOnlyChip record={record} />
                  </Stack>
                );
              }}
            />
            <FunctionField
              label=""
              sortable={false}
              render={(record) => (
                <>
                  <EditButton
                    icon={<EditIcon fontSize="small" />}
                    label=""
                    sx={{
                      color: 'var(--color-primary)',
                      minWidth: 'unset !important',
                      padding: 'var(--space-2)',
                      borderRadius: 'var(--radius-md)',
                      '& .MuiButton-startIcon': { margin: 0 },
                      '&:hover': { backgroundColor: 'var(--color-primary-soft)' },
                    }}
                    to={`/locationsManagement/${id}/items/${record.id}`}
                  />
                  <IconButton
                    icon={<DeleteOutlineIcon />}
                    color="var(--color-error)"
                    onClick={() => {
                      setConfirmOpen(true);
                      setToDelete(record.id);
                    }}
                  />
                </>
              )}
            />
          </Datagrid>
        )}
      </List>
      <Confirm
        isOpen={confirmOpen}
        title={t('locationItems.confirmDeletion.confirmDeletionTitle')}
        content={t('locationItems.confirmDeletion.confirmDeletionBody')}
        confirm={t('common.delete')}
        cancel={t('common.cancel')}
        onConfirm={async () => {
          try {
            await restaurantItemService.deleteItem(toDelete!);
            notify(t('common.saved'), { type: 'success' });
            setConfirmOpen(false);
            setToDelete(null);
            refresh();
          } catch (error: any) {
            notify(error.response.data.error, { type: 'error' });
            console.error(error.response.data.error);
            Sentry.captureException(error);
            setConfirmOpen(false);
            setToDelete(null);
          }
        }}
        onClose={() => {
          setConfirmOpen(false);
          setToDelete(null);
        }}
      />
    </Box>
  );
};
